import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Colors, Icon, Icons, createClasses } from '@kp/react-ui';

const CHAT_INPUT_MAX_HEIGHT = 120;

const classes = createClasses({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    backgroundColor: Colors.Neutral.backgroundMedium,
    boxSizing: 'border-box',
  },
  inputInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '12px',
    borderRadius: '16px',
    backgroundColor: Colors.Neutral.backgroundHoverStrong,
    flex: 1,
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    // TODO: restore 'space-between' once the file-upload button is re-enabled.
    justifyContent: 'flex-end',
  },
  plusButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: Colors.Neutral.icon,
    flexShrink: 0,
  },
  input: {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.Neutral.text,
    resize: 'none',
    backgroundColor: 'transparent',
    maxHeight: `${CHAT_INPUT_MAX_HEIGHT}px`,
    overflowY: 'auto',
    '::placeholder': {
      color: Colors.Neutral.textInactive,
    },
  },
  sendButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: Colors.Selected.backgroundStrong,
    color: Colors.Selected.textStrong,
    cursor: 'pointer',
    flexShrink: 0,
    ':disabled': {
      backgroundColor: Colors.Neutral.backgroundInactive,
      cursor: 'not-allowed',
    },
  },
});

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled }) => {
  const { t } = useTranslation('chat');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;

    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();

    if (!trimmed || disabled) return;

    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const isSendDisabled = disabled || !value.trim();

  return (
    <div className={classes.root}>
      <form className={classes.inputInner} action={submit}>
        <textarea
          ref={textareaRef}
          className={classes.input}
          placeholder={t('inputPlaceholder')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <div className={classes.actionsRow}>
          {/* TODO: file upload hidden for now — re-enable once file upload is supported.
          <button
            type="button"
            className={classes.plusButton}
            data-testid="chat-input-plus"
          >
            <Icon icon={Icons.IconAdd} size={20} />
          </button> */}
          <button
            type="submit"
            className={classes.sendButton}
            disabled={isSendDisabled}
            data-testid="chat-input-send"
          >
            <Icon icon={Icons.IconArrowUpward} size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};
